import {
  CellId,
  FilledDynamicValueTableColumnType,
  SemanticAwareColumn,
  SemanticAwareFieldGroup,
  SemanticDatasetName,
  assertNever,
  exploreSpecActions,
} from "@hex/common";
import { filter } from "fuzzaldrin-plus";
import { type JSX, default as React, memo, useCallback, useMemo } from "react";
import styled, { css } from "styled-components";

import { getFieldGroupUniformQueryPath } from "../../explore-v2/exploreFieldUtils.js";
import { useExploreSpecSelector } from "../../explore-v2/hooks/exploreCellContentsStateHooks.js";
import { useUpdateExploreSpec } from "../../explore-v2/hooks/useUpdateExploreSpec.js";
import { ExistingJoinConfig } from "../../explore-v2/joins/ExploreConfigureJoin.js";
import {
  HexButton,
  HexClasses,
  HexCollapse,
  HexMenu,
  HexNonIdealState,
  HexTooltip,
} from "../../hex-components/index.js";
import { useCellContentsSelector } from "../../hex-version-multiplayer/state-hooks/cellContentsStateHooks.js";
import { useValidateMagicJoin } from "../../magic/useValidateMagicJoin.js";
import { trackCounterEvent } from "../../util/trackEvent.js";
import { DataSourceTablePill } from "../cell/shared/scope-select/DataSourceTablePill.js";
import { Heading } from "../Heading.js";
import { AddSmallIcon } from "../icons/CustomIcons.js";
import { useTableCalcsContext } from "../renderer/table/calcs/TableCalcContext.js";

export interface SemanticAwareFieldListProps {
  fieldGroups: SemanticAwareFieldGroup[];
  renderListItem: (
    item: SemanticAwareColumn,
    meta: {
      isMeasure: boolean;
    },
  ) => JSX.Element | null;
  className?: string;
  disabled?: boolean;
  query?: string;
  headerRightElement?: (props: {
    uniformQueryPath: SemanticDatasetName[] | null;
    joinConfig?: ExistingJoinConfig;
  }) => JSX.Element | null;
  sort?: SemanticAwareFieldListSort;
  cellId: CellId;
  calcColumnIds: Set<string>;
  showAddCalcButtons?: boolean;
  readOnly: boolean;
  emptyState?: React.ReactNode;
}

export enum SemanticAwareFieldListSort {
  "SCHEMA" = "SCHEMA",
  "DATA_TYPE" = "DATA_TYPE",
  "ALPHANUMERIC" = "ALPHANUMERIC",
}

export interface SemanticAwareFieldListItemProps {
  field: SemanticAwareColumn;
  cellId: CellId;
  isMeasure: boolean;
}

export const SemanticAwareFieldList: React.ComponentType<SemanticAwareFieldListProps> =
  React.memo(function SemanticAwareFieldList(
    props: SemanticAwareFieldListProps,
  ) {
    const {
      calcColumnIds,
      cellId,
      className,
      disabled = false,
      emptyState,
      headerRightElement,
      query,
      readOnly,
      renderListItem,
      showAddCalcButtons = false,
      sort,
      ...rest
    } = props;

    let contents;
    let showHeaders = false;

    const hasSemanticInput = useCellContentsSelector({
      cellId,
      selector: (c) => {
        if (c.__typename !== "ExploreCell") {
          return false;
        }
        return c.semanticProjectId != null;
      },
    });

    const filterFields = useCallback(
      (items: SemanticAwareColumn[]) => {
        if (query && query.length > 0) {
          return filter(items, query, {
            key: "columnId",
            usePathScoring: false,
          });
        } else {
          return items;
        }
      },
      [query],
    );

    const fieldGroups = useMemo(() => {
      return sort != null
        ? sortFieldGroups(rest.fieldGroups, sort, calcColumnIds)
        : rest.fieldGroups;
    }, [rest.fieldGroups, sort, calcColumnIds]);

    const filteredGroupElements = fieldGroups.flatMap((group, idx) => {
      const isBaseFieldGroup = idx === 0;
      const dimensions = filterFields(group.columns);
      const measures = filterFields(group.measures);

      const nestedGroups = group.children;
      const filteredNestedGroupsElements =
        nestedGroups.flatMap((nestedGroup) => {
          const nestedDimensions = filterFields(nestedGroup.columns);
          const nestedMeasures = filterFields(nestedGroup.measures);

          if (nestedDimensions.length !== 0 || nestedMeasures.length !== 0) {
            return [
              <FieldGroupWithCollapsibleHeader
                key={nestedGroup.name}
                cellId={cellId}
                dimensions={nestedDimensions}
                disabled={disabled}
                fieldGroup={nestedGroup}
                hasSemanticInput={hasSemanticInput}
                headerRightElement={headerRightElement}
                isBaseFieldGroup={isBaseFieldGroup}
                measures={nestedMeasures}
                nested={true}
                readOnly={readOnly}
                renderListItem={renderListItem}
                showAddCalcButtons={showAddCalcButtons}
                title={nestedGroup.title}
              />,
            ];
          }
          return [];
        }) ?? [];

      // if there is only a single field group and it doesn't have any
      // nested groups, then no need to render collapsible header
      showHeaders = fieldGroups.length > 1 || nestedGroups.length > 0;

      if (
        dimensions.length !== 0 ||
        measures.length !== 0 ||
        filteredNestedGroupsElements.length !== 0
      ) {
        if (showHeaders) {
          return [
            <FieldGroupWithCollapsibleHeader
              key={group.name}
              cellId={cellId}
              dimensions={dimensions}
              disabled={disabled}
              fieldGroup={group}
              hasSemanticInput={hasSemanticInput}
              headerRightElement={headerRightElement}
              isBaseFieldGroup={isBaseFieldGroup}
              measures={measures}
              nestedGroupsElements={filteredNestedGroupsElements}
              readOnly={readOnly}
              renderListItem={renderListItem}
              showAddCalcButtons={showAddCalcButtons}
              title={group.title}
            />,
          ];
        }
        return [
          <FieldGroup
            key={group.name}
            cellId={cellId}
            dimensions={dimensions}
            disabled={disabled}
            isBaseFieldGroup={isBaseFieldGroup}
            measures={measures}
            readOnly={readOnly}
            renderListItem={renderListItem}
            showAddCalcButtons={showAddCalcButtons}
            title={group.title}
          />,
        ];
      }
      return [];
    });

    contents = filteredGroupElements;

    if (filteredGroupElements.length === 0) {
      contents = (
        <HexNonIdealState
          $autoHeight={true}
          $minimal={true}
          $small={true}
          title={emptyState ?? "No fields found"}
        />
      );
    }

    return (
      <FieldsContainer $hasGroupHeader={showHeaders} className={className}>
        {contents}
      </FieldsContainer>
    );
  });

interface FieldGroupProps {
  title: string;
  dimensions: SemanticAwareColumn[];
  measures: SemanticAwareColumn[];
  renderListItem: (
    item: SemanticAwareColumn,
    meta: {
      isMeasure: boolean;
    },
  ) => JSX.Element | null;
  disabled: boolean;
  cellId: CellId;
  nested?: boolean;
  isBaseFieldGroup: boolean;
  showAddCalcButtons: boolean;
  readOnly: boolean;
}

const FieldGroup: React.ComponentType<FieldGroupProps> = React.memo(
  function FieldGroup({
    cellId,
    dimensions,
    disabled,
    isBaseFieldGroup,
    measures,
    nested,
    readOnly,
    renderListItem,
    showAddCalcButtons,
    title,
  }) {
    return (
      <FieldGroupContainer>
        {measures.length > 0 && (
          <List $disabled={disabled}>
            <FieldListSubheaderContainer id={`${title}-measures`}>
              <Heading renderAs="h5">Measures</Heading>
              {isBaseFieldGroup && !nested && showAddCalcButtons && (
                <AddCalcButton
                  calcType="measure"
                  cellId={cellId}
                  disabled={readOnly}
                />
              )}
            </FieldListSubheaderContainer>
            {measures.map((item) => renderListItem(item, { isMeasure: true }))}
          </List>
        )}
        <List $disabled={disabled}>
          {measures.length > 0 && (
            <FieldListSubheaderContainer id={`${title}-dimensions`}>
              <Heading renderAs="h5">Dimensions</Heading>
              {isBaseFieldGroup && !nested && showAddCalcButtons && (
                <AddCalcButton
                  calcType="dimension"
                  cellId={cellId}
                  disabled={readOnly}
                />
              )}
            </FieldListSubheaderContainer>
          )}
          {dimensions.map((item) => renderListItem(item, { isMeasure: false }))}
        </List>
      </FieldGroupContainer>
    );
  },
);

type FieldGroupWithCollapsibleHeaderProps = FieldGroupProps & {
  nested?: boolean;
  nestedGroupsElements?: React.ReactNode[];
  fieldGroup: SemanticAwareFieldGroup;
  headerRightElement?: SemanticAwareFieldListProps["headerRightElement"];
  hasSemanticInput: boolean;
};

const FieldGroupWithCollapsibleHeader: React.ComponentType<FieldGroupWithCollapsibleHeaderProps> =
  React.memo(function FieldGroupWithCollapsibleHeader(props) {
    const {
      fieldGroup,
      headerRightElement,
      nested,
      nestedGroupsElements,
      title,
    } = props;

    const uniformQueryPath = useMemo(
      () => getFieldGroupUniformQueryPath(fieldGroup),
      [fieldGroup],
    );

    const {
      existingJoinConfig,
      hasError,
      hasMagicGeneratedJoin,
      hasTimeout,
      isLoading,
    } = useValidateMagicJoin({ queryPath: uniformQueryPath });

    return (
      <StyledHexCollapse
        $nested={nested}
        isOpenDefault={!hasMagicGeneratedJoin}
        small={true}
        title={
          <>
            <TitleContainer>
              <HexTooltip
                content="We configured an invalid join; please click edit to resolve issues."
                disabled={!hasError && !hasTimeout}
              >
                <StyledDataSourceTablePill
                  iconOverride={<></>}
                  loading={isLoading}
                  name={title}
                  pillTheme={hasError ? "RED" : undefined}
                  warningIntent={
                    hasTimeout ? "warning" : hasError ? "danger" : undefined
                  }
                />
              </HexTooltip>
              {headerRightElement?.({
                uniformQueryPath,
                joinConfig: existingJoinConfig,
              })}
            </TitleContainer>
          </>
        }
      >
        <FieldGroup {...props} />
        {nestedGroupsElements && <div>{nestedGroupsElements}</div>}
      </StyledHexCollapse>
    );
  });

const AddCalcButton = memo(function AddCalcButton({
  calcType,
  cellId,
  disabled,
}: {
  calcType: "measure" | "dimension";
  cellId: CellId;
  disabled: boolean;
}) {
  const viewType = useExploreSpecSelector({
    cellId,
    selector: (spec) => spec.viewType,
  });
  const updateExploreSpec = useUpdateExploreSpec(cellId);
  const { startEditingNewCalc } = useTableCalcsContext();
  const handleAddCalc = useCallback(() => {
    if (viewType === "visualization") {
      updateExploreSpec(exploreSpecActions.setViewType("both"));
    }
    startEditingNewCalc({
      isExploreMeasure: calcType === "measure",
    });
    trackCounterEvent(
      calcType === "measure"
        ? "explore.add_calc.field_list.add_measure"
        : "explore.add_calc.field_list.add_dimension",
    );
  }, [calcType, startEditingNewCalc, updateExploreSpec, viewType]);
  return (
    <HexTooltip content={`Add ${calcType}`} position="bottom">
      <FieldListSubheaderActions>
        <HexButton
          disabled={disabled}
          extraSmall={true}
          icon={<AddSmallIcon />}
          minimal={true}
          onClick={handleAddCalc}
        />
      </FieldListSubheaderActions>
    </HexTooltip>
  );
});

function sortFieldGroups(
  fieldGroups: SemanticAwareFieldGroup[],
  sort: SemanticAwareFieldListSort,
  calcColumnIds: Set<string>,
): SemanticAwareFieldGroup[] {
  return fieldGroups.map((fieldGroup) => {
    return {
      ...fieldGroup,
      columns: sortFields(fieldGroup.columns, sort, calcColumnIds),
      measures: sortFields(fieldGroup.measures, sort, calcColumnIds),
    };
  });
}

/**
 * Places calculations and aggregated dimensions first, as they are both
 * created as part of this exploration, not modeled upstream.
 */
function getAdhocColumnsFirstSorter(calcColumnIds: Set<string>) {
  return (a: SemanticAwareColumn, b: SemanticAwareColumn) => {
    const aIsCalc = calcColumnIds.has(a.columnId);
    const bIsCalc = calcColumnIds.has(b.columnId);

    if (aIsCalc && !bIsCalc) return -1;
    if (!aIsCalc && bIsCalc) return 1;

    const aIsAggDim = Boolean(a.columnAggregation);
    const bIsAggDim = Boolean(b.columnAggregation);
    if (aIsAggDim && !bIsAggDim) return -1;
    if (!aIsAggDim && bIsAggDim) return 1;

    return 0;
  };
}

function sortFields(
  fields: SemanticAwareColumn[],
  sort: SemanticAwareFieldListSort,
  calcColumnIds: Set<string>,
): SemanticAwareColumn[] {
  const groupingSorter = getAdhocColumnsFirstSorter(calcColumnIds);
  const alphaSort = (a: SemanticAwareColumn, b: SemanticAwareColumn): number =>
    a.columnName.localeCompare(b.columnName);
  switch (sort) {
    case SemanticAwareFieldListSort.SCHEMA:
      return fields.toSorted(groupingSorter);
    case SemanticAwareFieldListSort.DATA_TYPE:
      return fields.toSorted((a, b) => {
        const groupSort = groupingSorter(a, b);
        if (groupSort !== 0) return groupSort;
        const aTypeValue = columnTypeToSortValue(a.columnType);
        const bTypeValue = columnTypeToSortValue(b.columnType);
        if (aTypeValue !== bTypeValue) return aTypeValue - bTypeValue;
        return alphaSort(a, b);
      });
    case SemanticAwareFieldListSort.ALPHANUMERIC:
      return fields.toSorted((a, b) => {
        const groupSort = groupingSorter(a, b);
        if (groupSort !== 0) return groupSort;
        return alphaSort(a, b);
      });
    default:
      return assertNever(sort, sort);
  }
}

function columnTypeToSortValue(
  columnType: FilledDynamicValueTableColumnType,
): number {
  switch (columnType) {
    case "DATETIMETZ":
    case "DATETIME":
    case "DATE":
    case "TIME":
      return 0;
    case "NUMBER":
      return 1;
    case "STRING":
    case "UNKNOWN":
      return 2;
    case "BOOLEAN":
      return 3;
    default:
      assertNever(columnType, columnType);
  }
}

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  /* height of xs button */
  min-height: 21px;
  gap: 12px;
`;

const StyledDataSourceTablePill = styled(DataSourceTablePill)`
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  overflow: hidden;
`;

const FieldsContainer = styled.div<{ $hasGroupHeader: boolean }>`
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  margin: 0 -14px 0 -16px;
  padding-left: ${({ $hasGroupHeader }) => ($hasGroupHeader ? `20px` : `16px`)};

  padding-right: 4px;
`;

const FieldGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

const StyledHexCollapse = styled(HexCollapse)<{
  $nested?: boolean;
}>`
  .${HexClasses.HEX_COLLAPSE_TOGGLE_HEADER} {
    display: flex;
    flex-direction: column;
    position: sticky;
    overflow: hidden;
    z-index: ${({ $nested }) => ($nested ? 0 : 1)};
    padding: 8px 2px;
    margin-left: -16px;
    width: calc(100% + 16px);
    padding-top: 8px;
    top: ${({ $nested }) => ($nested ? `26px` : 0)};
    background-color: ${({ theme }) => theme.backgroundColor.DEFAULT};
  }

  padding-left: ${({ $nested }) => ($nested ? "16px" : 0)};

  .${HexClasses.HEX_COLLAPSE_HEADER} {
    overflow: hidden;
    width: 100%;
  }
`;

const List = styled(HexMenu)<{ $disabled: boolean }>`
  padding: 0;

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.6;
      cursor: not-allowed;
    `}
`;

const FieldListSubheaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 2px 2px 2px 6px;
  width: 100%;
  cursor: default;
`;

const FieldListSubheaderActions = styled.div`
  opacity: 0.5;
  &:hover {
    opacity: 1;
  }
`;
